// 레이드 지휘소 창 본문(RAID-PRD 1·9절): 지휘관 하르칸(마을 ②③④) 창의 오른쪽 본문. 배너·권장 레벨 경고·이번 주 보상 n/3·초기화 시각·
// 파티(영웅 + 용병 3)·보상·패턴·출정 버튼. 입장은 GameCommand 'enterRaid'로만 보낸다(게임이 해금·장면을 다시 확인한다).
// 스타일은 장비 창 공용 클래스(dh-g-*)를 같이 쓴다. 좁은 창(휴대폰)에서는 보상·패턴이 한 줄로 쌓인다.
import type { GameCommand, GameView } from '../shared/types';
import { assetUrl } from '../pwa/assetMap';
import { FONT, FONT_NUM, el, hudUrl, portraitImage } from './dom';
import { raidNetEnabledHere } from '../shared/content/raidNetFlags';
import type { RaidRoomView } from '../shared/raidNet';
import { raidNetPanelModel, raidPanelModel, validRaidCode, type RaidRewardLine } from './raidModel';

import type { RaidDiff } from '../shared/content/raid';

/** 난이도 탭 선택(기기 전용, 저장하지 않는다) */
let raidDiffPick: RaidDiff = 'normal';
export function setRaidDiffPick(d: RaidDiff): void { raidDiffPick = d; }
export function getRaidDiffPick(): RaidDiff { return raidDiffPick; }

const ICON: Record<RaidRewardLine['icon'], string> = { gold: 'G', set: '◈', legend: '★', stone: '◆', protect: '✦' };
const ICON_COLOR: Record<RaidRewardLine['icon'], string> = { gold: '#ffd76a', set: '#3fd07a', legend: '#f2b233', stone: '#9fd0ff', protect: '#e9d8b4' };
const ROLE_COLOR: Record<string, string> = { hero: '#ff6a5a', tank: '#6aa8ff', archer: '#7fe070', priest: '#ffe08a' };

/** 다시 그릴 때를 정하는 열쇠(주 횟수·레벨·분 단위 시계) */
export function raidPanelKey(v: GameView, nowMs: number): string {
  const r = v.raid;
  return (r ? r.weekClears + '/' + r.weekLimit + (r.hardOpen ? 'H' : 'h') + (r.hardFirst ? 'F' : 'f') : '-') + ':' + raidDiffPick + ':' + v.growth.level + ':' + Math.floor(nowMs / 60000) + ':' + v.progress.highestTown;
}

function portrait(parent: HTMLElement, src: string, color: string, size: number): void {
  const w = el('div', 'flex:0 0 auto;width:' + size + 'px;height:' + size + 'px;border-radius:50%;overflow:hidden;border:2px solid ' + color + ';background:#0b1020;box-shadow:0 0 8px ' + color + '55;', parent);
  const im = el('img', 'width:100%;height:100%;object-fit:cover;display:block;', w);
  im.alt = ''; im.draggable = false; im.src = src;
  im.onerror = () => { im.style.visibility = 'hidden'; };
}

export function renderRaidPanel(body: HTMLElement, v: GameView, locked: boolean, send: (c: GameCommand) => void, nowMs = Date.now()): void {
  const r = v.raid;
  const m = raidPanelModel({
    heroLevel: v.growth.level, clears: r ? r.weekClears : 0, limit: r ? r.weekLimit : 3, town: v.flow.town,
    highestTown: v.progress.highestTown, protectOk: v.gear.protectUnlocked, locked, nowMs,
    diff: raidDiffPick, hardOpen: r?.hardOpen === true, hardFirst: r?.hardFirst === true,
  });
  const hard = m.diff === 'hard';
  const root = el('div', 'display:flex;flex-direction:column;gap:10px;', body);
  root.className = 'dh-g';
  root.dataset.testid = 'raid-panel';

  // 난이도 탭: 노말 / 하드(잠겼으면 자물쇠 + 열리는 조건)
  const tabs = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap;', root);
  tabs.dataset.testid = 'raid-diff-tabs';
  const tab = (label: string, d: RaidDiff, on: boolean, off: boolean): void => {
    const t = el('button', 'min-width:84px;font-size:14px;padding:6px 14px;' + (d === 'hard' ? 'border-color:#ff5a4a;' + (on ? 'background:linear-gradient(180deg,#a02818,#5a0e08);color:#fff;' : '') : ''), tabs, (off ? '🔒 ' : '') + label);
    t.type = 'button'; t.className = 'dh-g-btn' + (on ? ' pri' : ''); t.dataset.testid = 'raid-diff-' + d; t.disabled = off;
    t.setAttribute('aria-pressed', on ? 'true' : 'false');
    t.addEventListener('click', () => { raidDiffPick = d; body.textContent = ''; renderRaidPanel(body, v, locked, send, nowMs); });
  };
  tab('노말', 'normal', !hard, false);
  tab('하드', 'hard', hard, !m.hardOpen);
  if (m.hardLockNote) { const ln = el('span', 'font:800 12px ' + FONT + ';color:#ffb37a;', tabs, m.hardLockNote); ln.dataset.testid = 'raid-hard-lock'; }
  if (m.hardFirst) { const tt = el('span', 'padding:2px 12px;border-radius:10px;background:linear-gradient(180deg,#ffd76a,#c8963e);border:1px solid #fff3c0;font:900 12px ' + FONT + ';color:#2a1d05;', tabs, m.title); tt.dataset.testid = 'raid-title-badge'; }

  // 배너: 비룡 그림 + 이름·권장 레벨
  const ban = el('div', 'position:relative;border-radius:10px;overflow:hidden;border:2px solid ' + (hard ? '#ff3a2a' : '#c8963e') + ';' + (hard ? 'box-shadow:0 0 14px rgba(255,50,30,.55);' : '') + 'aspect-ratio:3/1;min-height:96px;background:linear-gradient(135deg,#3a0e08,#120610);', root);
  ban.dataset.testid = 'raid-banner';
  const bi = el('img', 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:50% 40%;' + (hard ? 'filter:saturate(1.5) hue-rotate(-12deg) contrast(1.1) brightness(.8);' : ''), ban);
  bi.alt = ''; bi.draggable = false; bi.src = assetUrl('ui/raid/banner_wyvern.webp');
  bi.onerror = () => { bi.style.display = 'none'; };
  if (hard) el('div', 'position:absolute;inset:0;background:radial-gradient(ellipse at 70% 40%,rgba(255,40,20,.28),rgba(120,0,0,.5));mix-blend-mode:multiply;pointer-events:none;', ban);
  el('div', 'position:absolute;inset:0;background:linear-gradient(90deg,rgba(10,6,14,.86) 0%,rgba(10,6,14,.35) 55%,rgba(10,6,14,0) 100%);', ban);
  const bt = el('div', 'position:absolute;left:14px;bottom:10px;display:flex;flex-direction:column;gap:4px;', ban);
  bt.dataset.testid = 'raid-banner-title';
  el('div', 'font:900 12px ' + FONT + ';letter-spacing:3px;color:#ffb070;', bt, hard ? '레이드 · 하드 · 4인 파티' : '레이드 · 4인 파티');
  el('div', 'font:900 26px ' + FONT + ';letter-spacing:2px;color:#fff;text-shadow:0 2px 6px #000;', bt, m.name);
  const lv = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap;', bt);
  el('span', 'padding:1px 9px;border-radius:9px;background:rgba(0,0,0,.6);border:1px solid #ffd76a;font:900 12px ' + FONT + ';color:#ffd76a;', lv, '권장 Lv ' + m.recLevel);
  const mine = el('span', 'padding:1px 9px;border-radius:9px;background:rgba(0,0,0,.6);border:1px solid ' + (m.underLevel ? '#ff7a5a' : '#7fe070') + ';font:900 12px ' + FONT + ';color:' + (m.underLevel ? '#ffb39a' : '#b8f0a8') + ';', lv, '내 Lv ' + m.heroLevel);
  mine.dataset.testid = 'raid-level';
  // 출정: 배너 오른쪽 아래(스크롤 없이 보이게). 막히면 이유를 버튼 위에
  const go = el('div', 'position:absolute;right:12px;bottom:10px;display:flex;flex-direction:column;align-items:flex-end;gap:4px;', ban);
  go.dataset.testid = 'raid-banner-go';
  if (m.blocked) el('span', 'padding:2px 8px;border-radius:6px;background:rgba(0,0,0,.65);font:800 12px ' + FONT + ';color:#ffb37a;', go, m.blocked);
  const b = el('button', 'min-width:132px;font-size:17px;box-shadow:0 3px 10px rgba(0,0,0,.6);', go, m.enterLabel);
  b.type = 'button';
  b.className = 'dh-g-btn pri';
  b.dataset.sfx = 'event';
  b.dataset.testid = 'raid-enter';
  b.dataset.focusKey = 'raid-enter';
  b.disabled = m.blocked !== '';
  b.addEventListener('click', () => send(hard ? { type: 'enterRaid', raid: m.raid, diff: 'hard' } : { type: 'enterRaid', raid: m.raid }));
  if (m.underLevel) {
    const w = el('div', 'padding:7px 10px;border-radius:8px;border:1px solid #ff7a5a;background:rgba(80,16,10,.55);font:800 12.5px ' + FONT + ';color:#ffd2c4;', root,
      '⚠ 권장 레벨보다 ' + (m.recLevel - m.heroLevel) + ' 낮아요. 용병이 함께해도 버티기 어려울 수 있어요.');
    w.dataset.testid = 'raid-level-warn';
  }

  // 같이 출정(4인): 공개 스위치(raidNetEnabledHere)가 켜진 곳에서만
  if (netClient && raidNetEnabledHere()) mountNetSection(root, netClient, m.blocked !== '', Date.now);

  // 이번 주 보상 + 초기화
  const wk = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;', root);
  const pips = el('div', 'display:flex;gap:4px;', wk);
  for (let k = 0; k < m.limit; k++) el('span', 'width:14px;height:14px;border-radius:4px;border:1.5px solid #ffd76a;background:' + (k < m.clears ? 'rgba(80,80,90,.7)' : 'linear-gradient(180deg,#ffd76a,#c8963e)') + ';', pips);
  const wl = el('span', 'font:900 14px ' + FONT + ';color:' + (m.practice ? '#cfd8f2' : '#ffd76a') + ';', wk, m.weekLine);
  wl.dataset.testid = 'raid-week';
  el('span', 'font:700 12px ' + FONT + ';color:#aeb9da;', wk, m.resetLine);
  el('span', 'font:700 12px ' + FONT + ';color:#ffd76a;', wk, '주 ' + m.limit + '회 · 노말·하드 합산').dataset.testid = 'raid-week-shared';

  // 파티
  el('div', 'font:900 13px ' + FONT + ';letter-spacing:2px;color:#e9d8b4;', root, '파티');
  const party = el('div', 'display:grid;grid-template-columns:repeat(auto-fit,minmax(124px,1fr));gap:6px;', root);
  party.dataset.testid = 'raid-party';
  const member = (src: string, role: string, name: string, title: string, line: string): void => {
    const c = el('div', 'display:flex;gap:8px;align-items:center;padding:6px 8px;border-radius:8px;background:rgba(10,14,30,.85);border:1px solid ' + ROLE_COLOR[role] + '66;', party);
    portrait(c, src, ROLE_COLOR[role], 40);
    const t = el('div', 'min-width:0;display:flex;flex-direction:column;gap:1px;', c);
    el('div', 'font:900 13.5px ' + FONT + ';color:' + ROLE_COLOR[role] + ';', t, name + ' · ' + title);
    el('div', 'font:700 11.5px/1.3 ' + FONT + ';color:#cfd8f2;word-break:keep-all;', t, line);
  };
  member(hudUrl(portraitImage(Math.max(1, Math.min(3, v.stats.armorTier || 1)) as 1 | 2 | 3)), 'hero', v.heroId === 'lyra' ? '라이라' : '카엘', '나', '패턴을 피하며 딜을 넣어요');
  for (const p of m.party) member(assetUrl('ui/raid/merc_' + p.role + '.webp'), p.role, p.name, p.title, p.line);

  // 보상 | 패턴
  const cols = el('div', 'display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:8px;', root);
  const rw = el('div', 'padding:8px 10px;border-radius:8px;background:rgba(10,14,30,.85);border:1px solid #c8963e66;display:flex;flex-direction:column;gap:4px;', cols);
  rw.dataset.testid = 'raid-rewards';
  el('div', 'font:900 13px ' + FONT + ';letter-spacing:2px;color:#e9d8b4;', rw, m.practice ? '보상(다음 주에 다시)' : '클리어 보상');
  for (const line of m.rewards) {
    const row = el('div', 'display:flex;align-items:baseline;gap:6px;opacity:' + (m.practice ? '.5' : '1') + ';', rw);
    el('span', 'flex:0 0 18px;text-align:center;font:900 13px ' + FONT_NUM + ';color:' + ICON_COLOR[line.icon] + ';', row, ICON[line.icon]);
    el('span', 'font:800 13px ' + FONT + ';color:#fff;', row, line.text);
    if (line.note) el('span', 'font:700 11px ' + FONT + ';color:#aeb9da;', row, line.note);
  }
  const pt = el('div', 'padding:8px 10px;border-radius:8px;background:rgba(10,14,30,.85);border:1px solid #ff7a5a55;display:flex;flex-direction:column;gap:4px;', cols);
  el('div', 'font:900 13px ' + FONT + ';letter-spacing:2px;color:#ffb070;', pt, '패턴');
  for (const p of m.patterns) el('div', 'font:700 12.5px ' + FONT + ';color:' + (hard && p.startsWith('잿불 낙인') ? '#ff9a7a' : '#f4ecd8') + ';', pt, '• ' + p);
  el('div', 'font:700 11.5px/1.35 ' + FONT + ';color:#aeb9da;word-break:keep-all;', pt, m.enrageLine);
  if (hard) {
    const fr = el('div', 'padding:6px 10px;border-radius:8px;border:1px solid #ffd76a88;background:rgba(60,40,6,.6);font:800 12.5px/1.35 ' + FONT + ';color:' + (m.hardFirst ? '#aeb9da' : '#ffd76a') + ';word-break:keep-all;', root, '★ ' + m.firstLine);
    fr.dataset.testid = 'raid-first-line';
  }

}

// ── 같이 출정(4인) 칸 ───────────────────────────────────────────────────────
// RaidClient 중 이 칸이 읽고 부르는 부분(시험은 가짜를 넘긴다). main.ts가 setRaidNetPanel로 한 번 붙인다(raidNetEnabledHere일 때만).
export interface RaidNetClientView {
  readonly status: 'idle' | 'connecting' | 'online' | 'retrying' | 'closed';
  readonly room: RaidRoomView | null;
  readonly you: number;
  readonly error: string;
  readonly revision: number;
  create(diff?: RaidDiff): void;
  join(code: string): void;
  leave(): void;
  ready(on: boolean): void;
  start(): void;
}
let netClient: RaidNetClientView | null = null;
let netDraft = '';
export function setRaidNetPanel(client: RaidNetClientView | null): void { netClient = client; }

/** 지휘소 창 안의 「같이 출정(4인)」 칸. 스스로 250ms마다 연결 상태·시각을 보고 다시 그린다(창이 닫히면 멈춘다) */
function mountNetSection(parent: HTMLElement, c: RaidNetClientView, blocked: boolean, nowFn: () => number): void {
  const box = el('div', 'display:flex;flex-direction:column;gap:7px;padding:9px 11px;border-radius:9px;background:rgba(10,14,30,.88);border:1.5px solid #6aa8ff88;', parent);
  box.dataset.testid = 'raid-net';
  let key = '';
  let ticks = 0;
  const paint = (): void => {
    const m = raidNetPanelModel({ status: c.status, room: c.room, you: c.you, error: c.error, nowMs: nowFn() });
    const k = c.revision + '|' + m.mode + '|' + m.fillLeft + '|' + m.error + '|' + blocked;
    if (k === key) return;
    key = k;
    box.textContent = '';
    const hd = el('div', 'display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;', box);
    el('span', 'font:900 14px ' + FONT + ';letter-spacing:2px;color:#9cc4ff;', hd, '같이 출정 (4인)');
    el('span', 'font:700 11.5px ' + FONT + ';color:#aeb9da;', hd, '빈자리는 용병이 채워요 · 보상은 우편함으로 와요');
    const btn = (label: string, fn: () => void, cls: string, testid: string, disabled = false): HTMLButtonElement => {
      const b = el('button', 'font-size:14px;padding:6px 12px;', box, label);
      b.type = 'button'; b.className = 'dh-g-btn' + (cls ? ' ' + cls : ''); b.dataset.testid = testid; b.disabled = disabled;
      b.addEventListener('click', fn);
      return b;
    };
    if (m.error) { const er = el('div', 'padding:4px 8px;border-radius:6px;background:rgba(90,20,14,.7);font:800 12px ' + FONT + ';color:#ffd2c4;', box, m.error); er.dataset.testid = 'raid-net-error'; }
    if (m.mode === 'idle' || m.mode === 'closed') {
      const row = el('div', 'display:flex;gap:8px;flex-wrap:wrap;align-items:center;', box);
      const mk = el('button', 'font-size:14px;padding:6px 12px;', row, '방 만들기');
      mk.type = 'button'; mk.className = 'dh-g-btn pri'; mk.dataset.testid = 'raid-net-create'; mk.disabled = blocked;
      // 지휘소에서 고른 난이도로 방을 연다(하드 탭이면 하드 방 — #337 Codex P2)
      mk.addEventListener('click', () => c.create(raidDiffPick));
      const inp = el('input', 'width:96px;padding:6px 8px;border-radius:6px;border:1px solid #6aa8ff88;background:#0b1020;color:#fff;font:900 15px ' + FONT_NUM + ';letter-spacing:3px;text-transform:uppercase;', row) as HTMLInputElement;
      inp.type = 'text'; inp.maxLength = 6; inp.placeholder = '코드'; inp.value = netDraft; inp.dataset.testid = 'raid-net-code-input';
      inp.setAttribute('autocomplete', 'off'); inp.setAttribute('autocapitalize', 'characters'); inp.setAttribute('aria-label', '레이드 방 코드');
      const jn = el('button', 'font-size:14px;padding:6px 12px;', row, '참가');
      jn.type = 'button'; jn.className = 'dh-g-btn'; jn.dataset.testid = 'raid-net-join'; jn.disabled = blocked || !validRaidCode(netDraft);
      inp.addEventListener('input', () => { netDraft = inp.value.toUpperCase(); inp.value = netDraft; jn.disabled = blocked || !validRaidCode(netDraft); });
      inp.addEventListener('keydown', (ev) => { ev.stopPropagation(); if (ev.key === 'Enter' && validRaidCode(netDraft)) c.join(netDraft); });
      jn.addEventListener('click', () => { if (validRaidCode(netDraft)) c.join(netDraft); });
      return;
    }
    if (m.mode === 'connecting' || m.mode === 'retrying') {
      el('div', 'font:800 12.5px ' + FONT + ';color:#cfd8f2;', box, m.mode === 'retrying' ? '연결이 끊겼어요 · 다시 붙는 중…' : '방에 들어가는 중…');
      btn('나가기', () => c.leave(), '', 'raid-net-leave');
      return;
    }
    // 방 코드
    const cr = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;', box);
    el('span', 'font:700 12px ' + FONT + ';color:#aeb9da;', cr, '방 코드');
    const code = el('span', 'padding:2px 10px;border-radius:6px;background:#0b1020;border:1px solid #ffd76a;font:900 20px ' + FONT_NUM + ';letter-spacing:5px;color:#ffd76a;user-select:all;', cr, m.code);
    code.dataset.testid = 'raid-net-code';
    if (m.weekLeft >= 0) el('span', 'font:700 11.5px ' + FONT + ';color:#aeb9da;', cr, '이번 주 보상 ' + m.weekLeft + '회 남음');
    // 파티 4칸
    const grid = el('div', 'display:grid;grid-template-columns:repeat(auto-fit,minmax(138px,1fr));gap:6px;', box);
    grid.dataset.testid = 'raid-net-party';
    for (const sl of m.slots) {
      const col = sl.kind === 'human' ? (sl.me ? '#ffd76a' : '#9cc4ff') : sl.kind === 'merc' ? ROLE_COLOR[sl.role] ?? '#aeb9da' : '#555d78';
      const cell = el('div', 'display:flex;flex-direction:column;gap:2px;padding:6px 8px;border-radius:8px;background:rgba(6,10,24,.9);border:1px solid ' + col + '88;opacity:' + (sl.kind === 'empty' || (sl.kind === 'human' && !sl.online) ? '.6' : '1') + ';', grid);
      cell.dataset.testid = 'raid-net-slot-' + sl.slot;
      const top = el('div', 'display:flex;align-items:center;gap:5px;', cell);
      el('span', 'font:900 13px ' + FONT + ';color:' + col + ';min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;', top, sl.title);
      if (sl.leader) el('span', 'padding:0 5px;border-radius:5px;background:#c8963e;font:900 10px ' + FONT + ';color:#1a1204;', top, '방장');
      if (sl.kind === 'human') el('span', 'margin-left:auto;font:900 11px ' + FONT + ';color:' + (sl.ready ? '#7fe070' : '#aeb9da') + ';', top, sl.ready ? '준비' : '대기');
      if (sl.sub) el('div', 'font:700 11px/1.3 ' + FONT + ';color:#cfd8f2;word-break:keep-all;', cell, sl.sub);
    }
    if (m.mode === 'count') el('div', 'font:900 13px ' + FONT + ';color:#ffd76a;', box, '곧 출발해요…');
    else if (m.mode === 'run') el('div', 'font:900 13px ' + FONT + ';color:#ffd76a;', box, '토벌 중이에요');
    else if (m.fillLine) { const fl = el('div', 'font:700 12px ' + FONT + ';color:#cfd8f2;', box, m.fillLine); fl.dataset.testid = 'raid-net-fill'; }
    const acts = el('div', 'display:flex;gap:8px;flex-wrap:wrap;', box);
    const mkBtn = (label: string, fn: () => void, cls: string, testid: string, disabled: boolean): void => {
      const b = el('button', 'font-size:14px;padding:6px 14px;', acts, label);
      b.type = 'button'; b.className = 'dh-g-btn' + (cls ? ' ' + cls : ''); b.dataset.testid = testid; b.disabled = disabled;
      b.addEventListener('click', fn);
    };
    if (m.mode === 'lobby') {
      mkBtn(m.meReady ? '준비 해제' : '준비', () => c.ready(!m.meReady), m.meReady ? '' : 'pri', 'raid-net-ready', !m.canReady || blocked);
      if (m.amLeader) mkBtn('출발', () => c.start(), 'pri', 'raid-net-start', !m.canStart || blocked);
    }
    mkBtn('나가기', () => c.leave(), '', 'raid-net-leave', false);
  };
  paint();
  const timer = window.setInterval(() => {
    ticks++;
    if (!box.isConnected && ticks > 2) { window.clearInterval(timer); return; }
    paint();
  }, 250);
}
